import {
  TrendingUp,
  FileText,
  BarChart3,
  Wrench,
  LucideIcon,
  Calculator,
  Percent,
  Coins,
  ArrowUpRight,
  Receipt,
  FileSearch,
  AlertTriangle,
  Building,
  PiggyBank,
  Ban,
  Wallet,
  Home,
  LineChart,
  AreaChart,
  CalendarDays,
  Scale,
  Landmark,
  ShieldCheck,
  Zap,
  Banknote
} from "lucide-react"

export type ToolCategory = "financial" | "lease-tenant" | "property-analysis" | "maintenance"

export interface ToolDef {
  slug: string
  title: string
  shortTitle: string
  description: string
  category: ToolCategory
  icon: LucideIcon
  priority: number
  difficulty: "easy" | "medium" | "hard"
  related: string[]
  keywords: string[]
  ctaText: string
}

export const TOOL_CATEGORIES: Record<ToolCategory, { label: string; icon: LucideIcon; color: string; description: string }> = {
  financial: {
    label: "Financial Calculators",
    icon: TrendingUp,
    color: "bg-violet-500/15 text-violet-600",
    description: "Yield, ROI, cash flow, taxes — know your numbers.",
  },
  "lease-tenant": {
    label: "Lease & Tenant Tools",
    icon: FileText,
    color: "bg-blue-500/15 text-blue-600",
    description: "Rent increases, prorated rent, late fees, security deposits.",
  },
  "property-analysis": {
    label: "Property Analysis",
    icon: BarChart3,
    color: "bg-teal-500/15 text-teal-600",
    description: "Buy vs rent, 1% rule, DTI, vacancy costs.",
  },
  maintenance: {
    label: "Maintenance & Costs",
    icon: Wrench,
    color: "bg-orange-500/15 text-orange-600",
    description: "Maintenance budgets, management fees, utility splits.",
  },
}

export const toolsRegistry: ToolDef[] = [
  {
    slug: "rental-yield-calculator",
    title: "Rental Yield Calculator",
    shortTitle: "Rental Yield",
    description: "Calculate gross and net rental yield to evaluate property performance.",
    category: "financial",
    icon: Percent,
    priority: 1,
    difficulty: "easy",
    related: ["cash-flow-calculator", "roi-calculator", "break-even-rent-calculator"],
    keywords: ["rental yield", "gross yield", "net yield", "property ROI", "UK yield"],
    ctaText: "Track actual rental yields automatically by linking your rent and expenses.",
  },
  {
    slug: "prorated-rent-calculator",
    title: "Prorated Rent Calculator",
    shortTitle: "Prorated Rent",
    description: "Calculate partial month rent for mid-month move-ins or move-outs.",
    category: "lease-tenant",
    icon: CalendarDays,
    priority: 2,
    difficulty: "easy",
    related: ["rent-increase-calculator", "late-fee-calculator"],
    keywords: ["prorated rent", "partial rent", "move in rent", "daily rent calculation"],
    ctaText: "Manage partial payments and automate invoicing within the platform.",
  },
  {
    slug: "cash-flow-calculator",
    title: "Rental Property Cash Flow Calculator",
    shortTitle: "Cash Flow",
    description: "Estimate monthly positive or negative cash flow from your rental.",
    category: "financial",
    icon: Coins,
    priority: 3,
    difficulty: "medium",
    related: ["rental-yield-calculator", "break-even-rent-calculator"],
    keywords: ["rental cash flow", "property income", "monthly profit", "expense deduction"],
    ctaText: "Monitor your actual monthly cash flows instantly from your dashboard.",
  },
  {
    slug: "rent-increase-calculator",
    title: "Rent Increase Calculator",
    shortTitle: "Rent Increase",
    description: "Determine new rental rates and compounding increases over time.",
    category: "lease-tenant",
    icon: ArrowUpRight,
    priority: 4,
    difficulty: "easy",
    related: ["lease-expiry-tracker", "prorated-rent-calculator"],
    keywords: ["rent increase", "inflation rent increase", "annual rent review", "tenant increase"],
    ctaText: "Get automated reminders when rent increases are due for your active leases.",
  },
  {
    slug: "stamp-duty-calculator",
    title: "Stamp Duty Calculator",
    shortTitle: "Stamp Duty",
    description: "Estimate SDLT (Stamp Duty Land Tax) for property purchases.",
    category: "financial",
    icon: Landmark,
    priority: 5,
    difficulty: "medium",
    related: ["capital-gains-tax-estimator", "rental-yield-calculator"],
    keywords: ["stamp duty", "SDLT calculator", "buy to let stamp duty", "second home tax"],
    ctaText: "Keep all your property purchase documents and tax records in one secure place.",
  },
  {
    slug: "one-percent-rule-checker",
    title: "1% Rule Checker",
    shortTitle: "1% Rule",
    description: "Quickly assess if a property meets the 1% or 2% rule for cash flow viability.",
    category: "property-analysis",
    icon: Building,
    priority: 6,
    difficulty: "easy",
    related: ["cash-flow-calculator", "rental-yield-calculator"],
    keywords: ["1 percent rule", "2 percent rule", "rule of thumb real estate", "property screening"],
    ctaText: "Store potential properties on your dashboard alongside active ones.",
  },
  {
    slug: "vacancy-rate-cost-calculator",
    title: "Vacancy Rate Cost Calculator",
    shortTitle: "Vacancy Cost",
    description: "See exactly how much an empty property is costing you annually.",
    category: "property-analysis",
    icon: Ban,
    priority: 7,
    difficulty: "easy",
    related: ["break-even-rent-calculator", "cash-flow-calculator"],
    keywords: ["vacancy rate", "void periods", "empty property cost", "income lost"],
    ctaText: "Minimize vacancy periods with built-in lease expiry reminders.",
  },
  {
    slug: "break-even-rent-calculator",
    title: "Break-Even Rent Calculator",
    shortTitle: "Break-Even",
    description: "Find the exact minimum rent required to cover your property expenses.",
    category: "financial",
    icon: Scale,
    priority: 8,
    difficulty: "medium",
    related: ["cash-flow-calculator", "maintenance-budget-estimator"],
    keywords: ["break even rent", "minimum rent", "cover expenses", "operating costs"],
    ctaText: "Keep your expenses up to date to get real-time break-even alerts.",
  },
  {
    slug: "maintenance-budget-estimator",
    title: "Maintenance Budget Estimator",
    shortTitle: "Maintenance Budget",
    description: "Estimate annual repair and maintenance reserves using the 1% or square footage rules.",
    category: "maintenance",
    icon: Wrench,
    priority: 9,
    difficulty: "easy",
    related: ["property-management-fee-calculator", "cash-flow-calculator"],
    keywords: ["maintenance budget", "yearly repairs", "capex reserves", "property upkeep"],
    ctaText: "Log maintenance requests and track repair histories per property seamlessly.",
  },
  {
    slug: "late-fee-calculator",
    title: "Late Fee Calculator",
    shortTitle: "Late Fee",
    description: "Calculate maximum allowable late fees based on local regulations.",
    category: "lease-tenant",
    icon: AlertTriangle,
    priority: 10,
    difficulty: "easy",
    related: ["prorated-rent-calculator", "rent-increase-calculator"],
    keywords: ["late fee check", "overdue rent", "penalty calculator", "lease violation"],
    ctaText: "We automatically track overdue payments so you don't have to.",
  },
  {
    slug: "property-management-fee-calculator",
    title: "Property Management Fee Calculator",
    shortTitle: "Management Fee",
    description: "Compare the true cost of property management vs self-managing.",
    category: "maintenance",
    icon: Receipt,
    priority: 11,
    difficulty: "easy",
    related: ["cash-flow-calculator", "maintenance-budget-estimator"],
    keywords: ["management fees", "letting agent cost", "self manage vs agent"],
    ctaText: "Self-managing is easy when you use modern property tools. See for yourself.",
  },
  {
    slug: "buy-vs-rent-calculator",
    title: "Buy vs Rent Calculator",
    shortTitle: "Buy vs Rent",
    description: "Compare long-term wealth growth between buying property and renting.",
    category: "property-analysis",
    icon: Home,
    priority: 12,
    difficulty: "hard",
    related: ["price-to-rent-ratio-calculator", "mortgage-affordability-calculator"],
    keywords: ["buy or rent", "rent vs buy", "mortgage vs lease", "homeowner calculation"],
    ctaText: "Ready to move from renting to owning? Manage your very first property with us.",
  },
  {
    slug: "depreciation-calculator",
    title: "Rental Depreciation Calculator",
    shortTitle: "Depreciation",
    description: "Estimate allowable depreciation deductions for residential rental properties.",
    category: "financial",
    icon: AreaChart,
    priority: 13,
    difficulty: "medium",
    related: ["capital-gains-tax-estimator", "stamp-duty-calculator"],
    keywords: ["tax depreciation", "rental write-off", "cost basis", "useful life"],
    ctaText: "Easily categorize expenses to share with your accountant at tax time.",
  },
  {
    slug: "roi-calculator",
    title: "Return on Investment (ROI) Calculator",
    shortTitle: "ROI Calculation",
    description: "Calculate complete initial and annualized ROI for a real estate investment.",
    category: "financial",
    icon: LineChart,
    priority: 14,
    difficulty: "medium",
    related: ["cash-flow-calculator", "rental-yield-calculator"],
    keywords: ["real estate ROI", "return on investment", "property profit", "cash on cash"],
    ctaText: "Generate beautiful, comprehensive ROI reports automatically from your portfolio.",
  },
  {
    slug: "lease-expiry-tracker",
    title: "Lease Expiry Tracker",
    shortTitle: "Lease Expiry",
    description: "Find out exactly how many days are left on a lease and when to notify tenants.",
    category: "lease-tenant",
    icon: FileSearch,
    priority: 15,
    difficulty: "easy",
    related: ["rent-increase-calculator", "vacancy-rate-cost-calculator"],
    keywords: ["lease end date", "notice period tracker", "tenant renewal"],
    ctaText: "Get automated email alerts 60 days before any lease expires.",
  },
  {
    slug: "debt-to-income-calculator",
    title: "Debt-to-Income (DTI) Calculator",
    shortTitle: "DTI Check",
    description: "Calculate the DTI ratio for your prospective tenants to gauge their affordability.",
    category: "property-analysis",
    icon: Wallet,
    priority: 16,
    difficulty: "easy",
    related: ["mortgage-affordability-calculator", "buy-vs-rent-calculator"],
    keywords: ["tenant DTI", "debt to income limit", "tenant screening ratio"],
    ctaText: "Store tenant income details securely and review applicant health in seconds.",
  },
  {
    slug: "capital-gains-tax-estimator",
    title: "Capital Gains Tax Estimator",
    shortTitle: "Capital Gains",
    description: "Estimate CGT liability if you were to sell your rental property today.",
    category: "financial",
    icon: PiggyBank,
    priority: 17,
    difficulty: "hard",
    related: ["stamp-duty-calculator", "depreciation-calculator"],
    keywords: ["CGT property", "capital gains tax", "selling rental property tax"],
    ctaText: "Keep an immutable log of capital improvements to reduce future tax burdens.",
  },
  {
    slug: "security-deposit-interest-calculator",
    title: "Security Deposit Interest Calculator",
    shortTitle: "Deposit Interest",
    description: "Calculate mandated interest on tenant security deposits for your jurisdiction.",
    category: "lease-tenant",
    icon: ShieldCheck,
    priority: 18,
    difficulty: "medium",
    related: ["prorated-rent-calculator", "late-fee-calculator"],
    keywords: ["deposit interest", "tenant deposit return", "escrow interest calc"],
    ctaText: "Track security deposit balances per lease safely and securely.",
  },
  {
    slug: "price-to-rent-ratio-calculator",
    title: "Price-to-Rent Ratio Calculator",
    shortTitle: "Price-to-Rent",
    description: "Evaluate whether it is better to buy or rent in a specific housing market.",
    category: "property-analysis",
    icon: BarChart3,
    priority: 19,
    difficulty: "easy",
    related: ["buy-vs-rent-calculator", "one-percent-rule-checker"],
    keywords: ["price to rent", "housing market value", "rent or buy metric"],
    ctaText: "Analyze local markets and add prospective purchases directly to your account.",
  },
  {
    slug: "utilities-cost-splitter",
    title: "Utilities Cost Splitter",
    shortTitle: "Utility Splitter",
    description: "Fairly divide utility bills among multiple tenants or roommates.",
    category: "maintenance",
    icon: Zap,
    priority: 20,
    difficulty: "easy",
    related: ["maintenance-budget-estimator", "prorated-rent-calculator"],
    keywords: ["bill splitting", "HMO utilities", "share energy bills", "tenant charge"],
    ctaText: "Forward invoices and utility charges directly to tenants for easy tracking.",
  },
  {
    slug: "mortgage-affordability-calculator",
    title: "Mortgage Affordability Calculator",
    shortTitle: "Affordability",
    description: "Estimate how much you can afford to borrow for your next investment.",
    category: "financial",
    icon: Banknote,
    priority: 21,
    difficulty: "medium",
    related: ["debt-to-income-calculator", "roi-calculator"],
    keywords: ["mortgage limits", "buy to let mortgage", "borrowing power"],
    ctaText: "Ready to scale? Make managing a growing portfolio effortless.",
  }
]

export function getToolBySlug(slug: string): ToolDef | undefined {
  return toolsRegistry.find((t) => t.slug === slug)
}

export function getToolsByCategory(category: ToolCategory): ToolDef[] {
  return toolsRegistry.filter((t) => t.category === category).sort((a, b) => a.priority - b.priority)
}
